import React, { useState, useEffect } from 'react';
import { 
  Modal,
  Form,
  Input,
  Select,
  Button,
  Row,
  Col,
  Typography,
  message
} from 'antd';
import { 
  SaveOutlined,
  CloseOutlined
} from '@ant-design/icons';
import api from '../../services/api';

const { Text } = Typography;
const { Option } = Select;

// Global cache for countries, states, and cities
const dataCache = {
  countries: null as any[] | null,
  states: new Map<string, any[]>(),
  cities: new Map<string, any[]>(),
  loadingStates: new Set<string>(),
  loadingCities: new Set<string>()
};

// Pre-load countries data when module loads
const preloadCountries = async () => {
  if (!dataCache.countries) {
    try {
      const response = await api.get('/api/countries/');
      dataCache.countries = response.data?.data ?? response.data;
    } catch (error) {
      console.error('Error preloading countries:', error);
    }
  }
};

// Start preloading immediately
preloadCountries();

interface EditLocationModalProps {
  visible: boolean;
  onClose: () => void;
  locationData: {
    id: string;
    userid: string;
    company: string;
    address: string;
    city: string;
    state: string;
    country: string;
    zip: string;
    website_validation_notes: string;
    website_found_address: string;
    website_found_city: string;
    website_found_state: string;
    website_found_country: string;
  } | null;
  onLocationUpdated: () => void;
}

const EditLocationModal: React.FC<EditLocationModalProps> = ({
  visible,
  onClose,
  locationData,
  onLocationUpdated
}) => {
  const [form] = Form.useForm();

  // Add spinner animation CSS if not already added
  React.useEffect(() => {
    if (!document.getElementById('edit-modal-spinner-style')) {
      const style = document.createElement('style');
      style.id = 'edit-modal-spinner-style';
      style.textContent = `
        @keyframes spin {
          0% { transform: rotate(0deg); }
          100% { transform: rotate(360deg); }
        }
      `;
      document.head.appendChild(style);
    }
  }, []);
  const [loading, setLoading] = useState(false);
  const [countries, setCountries] = useState<any[]>([]);
  const [states, setStates] = useState<any[]>([]);
  const [cities, setCities] = useState<any[]>([]);
  const [statesLoading, setStatesLoading] = useState(false);
  const [citiesLoading, setCitiesLoading] = useState(false);
  const [customStateValue, setCustomStateValue] = useState('');
  const [customCityValue, setCustomCityValue] = useState('');

  // Load countries with caching
  const loadCountries = async () => {
    try {
      // Return cached data if available
      if (dataCache.countries) {
        setCountries(dataCache.countries);
        return dataCache.countries;
      }

      const response = await api.get('/api/countries/');
      const countriesData = response.data?.data ?? response.data;
      dataCache.countries = countriesData;
      setCountries(countriesData);
      return countriesData;
    } catch (error) {
      console.error('Error loading countries:', error);
      message.error('Failed to load countries');
      return [];
    }
  };

  // Load states for a country with caching
  const loadStates = async (countryId: string): Promise<any[]> => {
    const cacheKey = countryId;
    
    // Return cached data if available
    if (dataCache.states.has(cacheKey)) {
      const cachedStates = dataCache.states.get(cacheKey)!;
      setStates(cachedStates);
      return cachedStates;
    }

    // Prevent multiple simultaneous requests
    if (dataCache.loadingStates.has(cacheKey)) {
      return [];
    }

    setStatesLoading(true);
    dataCache.loadingStates.add(cacheKey);

    try {
      // Find country name from countryId
      const selectedCountry = (dataCache.countries || countries).find(country => country.country_id === countryId);
      const countryName = selectedCountry ? (selectedCountry.short_name || selectedCountry.name) : '';
      
      if (countryName) {
        const response = await api.get(`/api/states/?country=${encodeURIComponent(countryName)}`);
        const cleanStates = response.data
          .map((state: any) => {
            if (typeof state === 'string') {
              return {
                state_id: state.trim(),
                name: state.trim()
              };
            }
            return {
              state_id: state.state_id || state.name,
              name: (state.name || state).trim()
            };
          })
          .filter((state: any) => state.name && state.name.length > 0 && !state.name.includes('?') && !state.name.includes('(') && state.name !== 'Canada');
        
        // Cache the result
        dataCache.states.set(cacheKey, cleanStates);
        setStates(cleanStates);
        return cleanStates;
      }
      return [];
    } catch (error) {
      console.error('Error loading states:', error);
      message.error('Failed to load states');
      return [];
    } finally {
      setStatesLoading(false);
      dataCache.loadingStates.delete(cacheKey);
    }
  };

  // Load cities for a state with caching
  const loadCities = async (stateId: string): Promise<any[]> => {
    const countryId = form.getFieldValue('country');
    const cacheKey = `${countryId}-${stateId}`;
    
    // Return cached data if available
    if (dataCache.cities.has(cacheKey)) {
      const cachedCities = dataCache.cities.get(cacheKey)!;
      setCities(cachedCities);
      return cachedCities;
    }

    // Prevent multiple simultaneous requests
    if (dataCache.loadingCities.has(cacheKey)) {
      return [];
    }

    setCitiesLoading(true);
    dataCache.loadingCities.add(cacheKey);

    try {
      // Find state name from stateId
      const selectedState = states.find(state => state.state_id === stateId);
      const stateName = selectedState ? selectedState.name : '';
      
      // Find country name from the selected country in the form
      const selectedCountry = (dataCache.countries || countries).find(country => country.country_id === countryId);
      const countryName = selectedCountry ? (selectedCountry.short_name || selectedCountry.name) : '';
      
      if (stateName && countryName) {
        const response = await api.post('/api/cities/', {
          region: stateName,
          country: countryName
        });
        const cleanCities = response.data
          .map((city: any) => {
            if (typeof city === 'string') {
              return {
                city_id: city.trim(),
                name: city.trim()
              };
            }
            return {
              city_id: city.city_id || city.name,
              name: (city.name || city).trim()
            };
          })
          .filter((city: any) => city.name && city.name.length > 0);
        
        // Cache the result
        dataCache.cities.set(cacheKey, cleanCities);
        setCities(cleanCities);
        return cleanCities;
      }
      return [];
    } catch (error) {
      console.error('Error loading cities:', error);
      message.error('Failed to load cities');
      return [];
    } finally {
      setCitiesLoading(false);
      dataCache.loadingCities.delete(cacheKey);
    }
  };

  // Handle country change
  const handleCountryChange = async (countryId: string) => {
    form.setFieldsValue({ state: undefined, city: undefined });
    setStates([]);
    setCities([]);
    
    if (countryId) {
      await loadStates(countryId);
    }
  };

  // Handle state change
  const handleStateChange = async (stateId: string) => {
    form.setFieldsValue({ city: undefined });
    setCities([]);
    
    if (stateId) {
      await loadCities(stateId);
    }
  };

  // Handle form submission
  const handleSubmit = async () => {
    try {
      const values = await form.validateFields();
      setLoading(true);

      if (!locationData?.id) {
        message.error('Location ID is missing');
        return;
      }

      // For vendors, use the vendor ID directly in the API call
      const response = await api.put(`/api/diagnosys/locations/${locationData.id}/`, {
        city: values.city,
        state: values.state,
        country: values.country,
        address: values.address,
        pin: values.zipcode
      });

      if (response.data.status === 'success') {
        message.success('Location updated successfully');
        onLocationUpdated();
        onClose();
      } else {
        message.error('Failed to update location');
      }
    } catch (error) {
      console.error('Error updating location:', error);
      message.error('Error updating location. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  // Handle modal close
  const handleClose = () => {
    form.resetFields();
    setStates([]);
    setCities([]);
    setCustomStateValue('');
    setCustomCityValue('');
    onClose();
  };

  // Initialize form when modal opens
  useEffect(() => {
    if (visible && locationData) {
      // Reset dependent dropdowns first
      setStates([]);
      setCities([]);
      
      // Set initial form values immediately for fast modal opening
      form.setFieldsValue({
        country: undefined,
        state: undefined,
        city: undefined,
        address: locationData.address || '',
        zipcode: locationData.zip || ''
      });

      // Load data asynchronously after modal is open
      setTimeout(async () => {
        try {
          // Ensure countries are loaded first
          let countriesData = dataCache.countries;
          if (!countriesData) {
            countriesData = await loadCountries();
          }
          
          // Load dependent data asynchronously if country is available
          if (locationData.country && countriesData && countriesData.length > 0) {
            let countryIdToUse = null;
            let countryNameToUse = locationData.country;
            
            // Try to find country by name
            const matchedCountry = countriesData.find((country: any) => {
              const countryName = country.short_name || country.name;
              return countryName && countryName.toLowerCase() === locationData.country.toLowerCase();
            });
            
            if (matchedCountry) {
              countryIdToUse = matchedCountry.country_id;
              countryNameToUse = matchedCountry.short_name || matchedCountry.name;
            } else {
              console.warn('Country not found in dropdown:', locationData.country);
              message.warning(`Country "${locationData.country}" not found in dropdown`);
            }
            
            if (countryIdToUse && countryNameToUse) {
              // Set countries in state first to ensure the Select component has the options
              setCountries(countriesData);
              
              // Set country first
              form.setFieldsValue({ country: countryIdToUse });
              
              // Load states for the country
              try {
                setStatesLoading(true);
                
                const response = await api.get(`/api/states/?country=${encodeURIComponent(countryNameToUse)}`);
                const statesData = response.data
                  .map((state: any) => {
                    if (typeof state === 'string') {
                      return {
                        state_id: state.trim(),
                        name: state.trim()
                      };
                    }
                    return {
                      state_id: state.state_id || state.name,
                      name: (state.name || state).trim()
                    };
                  })
                  .filter((state: any) => state.name && state.name.length > 0 && !state.name.includes('?') && !state.name.includes('(') && state.name !== 'Canada');
                
                setStates(statesData);
                setStatesLoading(false);
                
                // Try to set the state value
                if (locationData.state && statesData.length > 0) {
                  let stateIdToSet = null;
                  let stateNameToUse = locationData.state;
                  
                  // Try to match by name
                  const stateByName = statesData.find((state: any) => 
                    state.name && state.name.toLowerCase().trim() === locationData.state.toLowerCase().trim()
                  );
                  
                  if (stateByName) {
                    stateIdToSet = stateByName.state_id;
                    stateNameToUse = stateByName.name;
                  }
                  
                  if (stateIdToSet && stateNameToUse) {
                    // Set state value
                    form.setFieldsValue({ state: stateIdToSet });
                    
                    // Load cities using the POST method
                    setCitiesLoading(true);
                    
                    const citiesResponse = await api.post('/api/cities/', {
                      region: stateNameToUse,
                      country: countryNameToUse
                    });
                    
                    const citiesData = citiesResponse.data
                      .map((city: any) => {
                        if (typeof city === 'string') {
                          return {
                            city_id: city.trim(),
                            name: city.trim()
                          };
                        }
                        return {
                          city_id: city.city_id || city.name,
                          name: (city.name || city).trim()
                        };
                      })
                      .filter((city: any) => city.name && city.name.length > 0);
                    
                    setCities(citiesData);
                    setCitiesLoading(false);
                    
                    // Try to set the city value
                    if (locationData.city && citiesData.length > 0) {
                      let cityIdToSet = null;
                      
                      // Try to match by name
                      const cityByName = citiesData.find((city: any) => 
                        city.name && city.name.toLowerCase().trim() === locationData.city.toLowerCase().trim()
                      );
                      
                      if (cityByName) {
                        cityIdToSet = cityByName.city_id;
                      }
                      
                      if (cityIdToSet) {
                        // Set city value
                        form.setFieldsValue({ city: cityIdToSet });
                      } else {
                        console.warn('City not found in dropdown:', locationData.city);
                      }
                    }
                  } else {
                    console.warn('State not found in dropdown:', locationData.state);
                  }
                }
              } catch (error) {
                console.error('Error loading states:', error);
                setStatesLoading(false);
                setCitiesLoading(false);
              }
            }
          }
        } catch (error) {
          console.error('Error loading dependent data:', error);
          message.error('Error loading location data. Please try again.');
          setStatesLoading(false);
          setCitiesLoading(false);
        }
      }, 100);
    }
  }, [visible, locationData]);

  return (
    <Modal
      title={`Edit Location - ${locationData?.company || ''}`}
      open={visible}
      onOk={handleSubmit}
      onCancel={handleClose}
      confirmLoading={loading}
      width={600}
      centered
      okText="Save Changes"
      cancelText="Cancel"
      okButtonProps={{
        icon: <SaveOutlined />,
        loading: loading
      }}
      cancelButtonProps={{
        icon: <CloseOutlined />
      }}
      bodyStyle={{
        maxHeight: '70vh',
        overflowY: 'auto',
        paddingBottom: '16px',
        paddingRight: '9px'
      }}
    >
      <Form
        form={form}
        layout="vertical"
        style={{ marginTop: '1rem' }}
      >
        <Row gutter={16}>
          <Col span={12}>
            <Form.Item
              label="Country"
              name="country"
              rules={[{ required: true, message: 'Please select country' }]}
            >
              <Select
                placeholder="Select Country"
                showSearch
                allowClear
                onChange={handleCountryChange}
                style={{ width: '100%' }}
                filterOption={(input, option) =>
                  (option?.children as unknown as string)?.toLowerCase().includes(input.toLowerCase())
                }
              >
                {countries.map(country => (
                  <Option key={country.country_id} value={country.country_id}>
                    {country.short_name || country.name}
                  </Option>
                ))}
              </Select>
            </Form.Item>
          </Col>
          <Col span={12}>
            <Form.Item
              label="State"
              name="state"
              rules={[{ required: true, message: 'Please select state' }]}
            >
              <Select
                placeholder="Select State"
                showSearch
                allowClear
                onChange={handleStateChange}
                style={{ width: '100%' }}
                disabled={!form.getFieldValue('country')}
                loading={statesLoading}
                notFoundContent={statesLoading ? (
                  <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '8px' }}>
                    <div style={{ 
                      width: '16px', 
                      height: '16px', 
                      border: '2px solid #f3f3f3', 
                      borderTop: '2px solid #1890ff', 
                      borderRadius: '50%', 
                      animation: 'spin 1s linear infinite' 
                    }}></div>
                    <span style={{ marginLeft: '8px', color: '#666' }}>Loading states...</span>
                  </div>
                ) : "No states found"}
                filterOption={(input, option) =>
                  (option?.children as unknown as string)?.toLowerCase().includes(input.toLowerCase())
                }
                onSearch={(value) => setCustomStateValue(value)}
                dropdownRender={(menu) => (
                  <div>
                    {menu}
                    {customStateValue && !states.some(state => 
                      state.name.toLowerCase() === customStateValue.toLowerCase()
                    ) && (
                      <div style={{ padding: '8px', borderTop: '1px solid #f0f0f0' }}>
                        <Button
                          type="text"
                          icon={<span style={{ color: '#52c41a' }}>+</span>}
                          onClick={() => {
                            const newState = {
                              state_id: `${customStateValue}`,
                              name: customStateValue
                            };
                            setStates(prev => [...prev, newState]);
                            form.setFieldsValue({ state: newState.state_id });
                            setCustomStateValue('');
                          }}
                          style={{ width: '100%', textAlign: 'left' }}
                        >
                          Add "{customStateValue}"
                        </Button>
                      </div>
                    )}
                  </div>
                )}
              >
                {states.map(state => (
                  <Option key={state.state_id} value={state.state_id}>
                    {state.name}
                  </Option>
                ))}
              </Select>
            </Form.Item>
          </Col>
        </Row>
        <Row gutter={16}>
          <Col span={12}>
            <Form.Item
              label="City"
              name="city"
              rules={[{ required: true, message: 'Please select city' }]}
            >
              <Select
                placeholder="Select City"
                showSearch
                allowClear
                style={{ width: '100%' }}
                disabled={!form.getFieldValue('state')}
                loading={citiesLoading}
                notFoundContent={citiesLoading ? (
                  <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '8px' }}>
                    <div style={{ 
                      width: '16px', 
                      height: '16px', 
                      border: '2px solid #f3f3f3', 
                      borderTop: '2px solid #52c41a', 
                      borderRadius: '50%', 
                      animation: 'spin 1s linear infinite' 
                    }}></div>
                    <span style={{ marginLeft: '8px', color: '#666' }}>Loading cities...</span>
                  </div>
                ) : "No cities found"}
                filterOption={(input, option) =>
                  (option?.children as unknown as string)?.toLowerCase().includes(input.toLowerCase())
                }
                onSearch={(value) => setCustomCityValue(value)}
                dropdownRender={(menu) => (
                  <div>
                    {menu}
                    {customCityValue && !cities.some(city => 
                      city.name.toLowerCase() === customCityValue.toLowerCase()
                    ) && (
                      <div style={{ padding: '8px', borderTop: '1px solid #f0f0f0' }}>
                        <Button
                          type="text"
                          icon={<span style={{ color: '#52c41a' }}>+</span>}
                          onClick={() => {
                            const newCity = {
                              city_id: `${customCityValue}`,
                              name: customCityValue
                            };
                            setCities(prev => [...prev, newCity]);
                            form.setFieldsValue({ city: newCity.city_id });
                            setCustomCityValue('');
                          }}
                          style={{ width: '100%', textAlign: 'left' }}
                        >
                          Add "{customCityValue}"
                        </Button>
                      </div>
                    )}
                  </div>
                )}
              >
                {cities.map(city => (
                  <Option key={city.city_id} value={city.city_id}>
                    {city.name}
                  </Option>
                ))}
              </Select>
            </Form.Item>
          </Col>
          <Col span={12}>
            <Form.Item
              label="Zip Code"
              name="zipcode"
              rules={[{ required: true, message: 'Please enter zip code' }]}
            >
              <Input 
                placeholder="Enter zip code"
                maxLength={20}
              />
            </Form.Item>
          </Col>
        </Row>
        <Form.Item
          label={
            <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between" }}>
              <span>Address</span>
              {/* <Text strong style={{ color: "#ff0404ff", fontSize: "10px", marginLeft: "10px" }}>
                Note - Format should be:
                {" "}Address , State Abbreviation Name Zipcode , Country Name{" "}
                (Example: 17850 S Western Ave., Gardena, CA 90248, United States)
              </Text> */}
            </div>
          }
          name="address"
          rules={[{ required: true, message: "Please enter address" }]}
        >
          <Input.TextArea 
            rows={3} 
            placeholder="Enter complete address"
          />
        </Form.Item>


        {locationData && (
          <div style={{ 
            background: '#f5f5f5', 
            padding: '1rem', 
            borderRadius: '8px',
            marginTop: '1rem'
          }}>
            <Text strong style={{ color: '#666', fontSize: '16px' }}>Website Validation Information:</Text>
            
            <div style={{ marginTop: '12px' }}>
              <Text strong style={{ color: '#666' }}>Website Address:</Text>
              <Text style={{ fontSize: '0.9rem' }}>
                {locationData.website_found_address || 'N/A'}
              </Text>
            </div>
            
            <div style={{ marginTop: '8px' }}>
              <Text strong style={{ color: '#666' }}>Website City:</Text>
              <Text style={{ fontSize: '0.9rem' }}>
                {locationData.website_found_city || 'N/A'}
              </Text>
            </div>
            
            <div style={{ marginTop: '8px' }}>
              <Text strong style={{ color: '#666' }}>Website State:</Text>
              <Text style={{ fontSize: '0.9rem' }}>
                {locationData.website_found_state || 'N/A'}
              </Text>
            </div>
            
            <div style={{ marginTop: '8px' }}>
              <Text strong style={{ color: '#666' }}>Website Country:</Text>
              <Text style={{ fontSize: '0.9rem' }}>
                {locationData.website_found_country || 'N/A'}
              </Text>
            </div>
            
            {locationData.website_validation_notes && (
              <div style={{ marginTop: '12px' }}>
                <Text strong style={{ color: '#666' }}>Validation Notes:</Text>
                <Text style={{ fontSize: '0.9rem', fontStyle: 'italic', lineHeight: '1.4' }}>
                  {locationData.website_validation_notes}
                </Text>
              </div>
            )}
          </div>
        )}
      </Form>
    </Modal>
  );
};

export default EditLocationModal;
